import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Divider,
  Stack,
  IconButton,
  Grid,
  Container,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ArrowLeft,
  MapPin,
  Mail,
  Globe,
  User,
  Briefcase,
  Calendar,
  TrendingUp,
  Users,
  FileText,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CompanyRecruitersCard from "../CompanyRecruitersCard";

const colors = {
  primary: '#0017E7',
  secondary: '#F4E1F7',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  text: '#1e293b',
  lightText: '#64748b',
  border: '#e2e8f0',
  background: '#fefcff',
};

const MainContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: '#ffffff',
  padding: theme.spacing(2),
  paddingTop: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(2),
  },
}));

const HeaderCard = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
  position: 'relative',
  boxShadow: '0 4px 20px rgba(0, 23, 231, 0.12)',
  border: `2px solid ${colors.primary}15`,
}));

const HeaderGradient = styled(Box)(({ theme }) => ({
  height: 100,
  background: `linear-gradient(135deg, ${colors.primary} 0%, #3b3bff 100%)`,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  },
}));

const LogoWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 50,
  left: theme.spacing(3),
  zIndex: 1,
  [theme.breakpoints.up('md')]: {
    left: theme.spacing(4),
  },
}));

const LogoAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  background: 'white',
  border: `4px solid ${colors.primary}`,
  boxShadow: `0 8px 24px ${colors.primary}40`,
  fontSize: '32px',
  fontWeight: 700,
  color: colors.primary,
  [theme.breakpoints.down('sm')]: {
    width: 80,
    height: 80,
    fontSize: '24px',
  },
}));

const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 16,
  border: `2px solid ${colors.primary}10`,
  boxShadow: '0 2px 12px rgba(0, 23, 231, 0.08)',
  transition: 'box-shadow 0.2s ease',
  background: 'white',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 23, 231, 0.12)',
  },
}));

const RecruiterCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  border: `2px solid ${colors.primary}10`,
  boxShadow: '0 2px 12px rgba(0, 23, 231, 0.08)',
  overflow: 'hidden',
  transition: 'box-shadow 0.2s ease',
  background: 'white',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 23, 231, 0.12)',
  },
}));

const ColorBar = styled(Box)({
  height: 4,
  background: `linear-gradient(90deg, ${colors.primary} 0%, #3b3bff 100%)`,
});

const IconBox = styled(Box)(({ bgColor }) => ({
  width: 56,
  height: 56,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: bgColor,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
}));

const InfoPaper = styled(Paper)({
  padding: 20,
  backgroundColor: colors.secondary,
  border: `2px solid ${colors.primary}15`,
  borderRadius: 16,
  boxShadow: 'none',
});

const getStatus = (isActive) => {
  if (!isActive) return { label: 'inactive', color: 'error' };
  return { label: 'active', color: 'success' };
};

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last || 'U';
};

const CompanyDetailsInterface = () => {
  const navigate = useNavigate();
  const { adminId: encodedAdminId } = useParams();
  const { t } = useTranslation();

  const [admin, setAdmin] = useState(null);
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [totalRecruiters, setTotalRecruiters] = useState(0);
  const [totalActiveRecruiters, setTotalActiveRecruiters] = useState(0);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const fetchCompanyData = async (currentPage = page, currentRowsPerPage = rowsPerPage) => {
    try {
      setLoading(true);
      setError(null);

      const adminResponse = await fetch(`${API_BASE}authentication/superadmin/admins/${encodedAdminId}/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!adminResponse.ok) {
        const errorData = await adminResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch admin details');
      }

      const adminData = await adminResponse.json();
      setAdmin(adminData.admin || adminData);

      const recruitersResponse = await fetch(`${API_BASE}authentication/superadmin/admins/${encodedAdminId}/recruiters/?page=${currentPage + 1}&page_size=${currentRowsPerPage}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!recruitersResponse.ok) {
        const errorData = await recruitersResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch recruiters');
      }

      const recruitersData = await recruitersResponse.json();
      setRecruiters(recruitersData.results || []);
      setTotalRecruiters(recruitersData.count || 0);
      setTotalActiveRecruiters(recruitersData.total_active_recruiters || 0);

    } catch (err) {
      console.error('Error fetching company data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (encodedAdminId) {
      fetchCompanyData();
    }
  }, [encodedAdminId, API_BASE]);

  useEffect(() => {
    if (encodedAdminId) {
      fetchCompanyData(page, rowsPerPage);
    }
  }, [page, rowsPerPage]);

  const totalPosts = recruiters.reduce((sum, r) => sum + (r.posts_count || 0), 0);
  const activePosts = recruiters.reduce((sum, r) => sum + (r.active_posts || 0), 0);
  const totalApplications = recruiters.reduce((sum, r) => sum + (r.applications_count || 0), 0);
  const activeRecruiters = recruiters.filter((r) => r.is_active).length;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <MainContainer>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} sx={{ color: colors.primary }} />
          </Box>
        </Container>
      </MainContainer>
    );
  }

  if (error) {
    return (
      <MainContainer>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              {t('superAdmin.companyDetailsInterface.errorLoadingCompanyDetails')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2, borderColor: colors.primary, color: colors.primary }}
              onClick={() => window.location.reload()}
            >
              {t('general.refresh')}
            </Button>
          </Box>
        </Container>
      </MainContainer>
    );
  }

  if (!admin) {
    return (
      <MainContainer>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {t('superAdmin.companyDetailsInterface.companyNotFound')}
            </Typography>
          </Box>
        </Container>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Container maxWidth="xl">
        <HeaderCard>
          <HeaderGradient />

          <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: 'black',
                width: 40,
                height: 40,
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          </Box>

          <LogoWrapper>
            <LogoAvatar src={admin.image}>
              {getInitials(admin.first_name, admin.last_name)}
            </LogoAvatar>
          </LogoWrapper>

          <Box sx={{ pt: { xs: 10, md: 8 }, pb: 3, px: { xs: 2, md: 4 } }}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: colors.text, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                    {admin.company}
                  </Typography>
                  <Chip
                    label={getStatus(admin.is_active).label.toUpperCase()}
                    color={getStatus(admin.is_active).color}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </Box>

                <Typography variant="body1" sx={{ color: colors.primary, mb: 1.5, fontWeight: 600 }}>
                  {admin.comp_industry}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.lightText, mb: 3, lineHeight: 1.7 }}>
                  {admin.comp_description}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <User size={18} color={colors.primary} />
                      <Typography variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                        {admin.first_name} {admin.last_name} (@{admin.username})
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Mail size={18} color={colors.primary} />
                      <Typography variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                        {admin.email}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <MapPin size={18} color={colors.primary} />
                      <Typography variant="body2" sx={{ color: colors.text, fontWeight: 500 }}>
                        {admin.address}, {admin.city}, {admin.postal_code}, {admin.country}
                      </Typography>
                    </Stack>
                  </Grid>
                  {admin.site_url && (
                    <Grid item xs={12} sm={6}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Globe size={18} color={colors.primary} />
                        <Typography
                          component="a"
                          href={admin.site_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{
                            color: colors.primary,
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {admin.site_url}
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} lg={4}>
                <InfoPaper>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <IconBox bgColor={colors.primary}>
                      <Users size={28} color="white" />
                    </IconBox>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: colors.primary, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {t('superAdmin.companyDetailsInterface.totalRecruiters')}
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 800, color: colors.text, lineHeight: 1 }}>
                        {totalRecruiters}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography variant="body2" sx={{ color: colors.success, fontWeight: 600 }}>
                    {t('superAdmin.companyDetailsInterface.activeRecruiters', { totalActiveRecruiters })}
                  </Typography>
                </InfoPaper>
              </Grid>
            </Grid>
          </Box>
        </HeaderCard>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {[
            { icon: FileText, label: t('superAdmin.companyDetailsInterface.totalPosts'), value: totalPosts, bgColor: '#e0e7ff', iconColor: colors.primary },
            { icon: TrendingUp, label: t('superAdmin.companyDetailsInterface.activePosts'), value: activePosts, bgColor: '#d1fae5', iconColor: colors.success },
            { icon: Briefcase, label: t('superAdmin.companyDetailsInterface.applications'), value: totalApplications, bgColor: '#fef3c7', iconColor: colors.warning },
            {
              icon: Calendar,
              label: t('superAdmin.companyDetailsInterface.memberSince'),
              value: admin.created_at ? new Date(admin.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A',
              bgColor: colors.secondary,
              iconColor: colors.primary,
            },
          ].map((stat, idx) => {
            const IconComp = stat.icon;
            return (
              <Grid item xs={6} md={3} key={idx}>
                <StatsCard>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <IconBox bgColor={stat.bgColor} sx={{ mb: 2 }}>
                      <IconComp size={24} color={stat.iconColor} />
                    </IconBox>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: colors.lightText, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      {stat.label}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: colors.text }}>
                      {stat.value}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.text, mb: 0.5 }}>
            {t('superAdmin.companyDetailsInterface.companyRecruiters')}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.lightText, fontWeight: 500 }}>
            {t('superAdmin.companyDetailsInterface.manageRecruiters')}
          </Typography>
        </Box>

        <CompanyRecruitersCard
          recruiters={recruiters}
          totalRecruiters={totalRecruiters}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          handleRowsPerPageChange={handleRowsPerPageChange}
        />
      </Container>
    </MainContainer>
  );
};

export default CompanyDetailsInterface;